import type { TransactionFiltersRawFormValues } from '@escrow/local-storage';
import type { Experiments } from '@freelancer/abtest/interface';
import type { AppInstallPromptDismissals } from '@freelancer/app-install-prompt';
import type {
  AiConsultantGeneratedProjectDraft,
  AiConsultantMessage,
  AiPlaygroundConversationSaveFile,
  AiPlaygroundSavedTool,
  BusinessBuilderBlueprint,
  BusinessBuilderChatHistory,
  PostJobPageBlueprintContext,
  TrackingConsent,
} from '@freelancer/datastore/collections';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type { PostJobPageFormStates } from '@freelancer/datastore/collections/post-job-page-form-state';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type { ProjectViewProject } from '@freelancer/datastore/collections/project-view-projects';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type { Task } from '@freelancer/datastore/collections/tasks';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type {
  ThreadContext,
  ThreadType,
} from '@freelancer/datastore/collections/threads';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type {
  ResumeParsingStatus,
  UserParsedResume,
} from '@freelancer/datastore/collections/user-parsed-resumes';
import type { CurrentTimeTrackingSession } from '@freelancer/project-time-tracking';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type { SearchFilters } from '@freelancer/search-filters/search-filters.model';
import type { Theme, ThemeSetting } from '@freelancer/theme';
import type { VideochatCalling } from '@freelancer/videochat';
import type { ViewedItemsMap } from '@freelancer/viewed-items';
import type { RoleApi } from 'api-typings/common/common';

/**
 *  Holds the types which can be stored in local storage i.e. are JSON serialisable.
 *  Values can be restricted to solely their type,
 *  or can also be allowed to be `null` (if the item needs to be able to be deleted).
 */
export interface LocalStorageTypes {
  /* FLN LOCAL STORAGE TYPES */
  readonly complianceQueueApproveDenyFormStates: {
    readonly [supportSessionId: number]: {
      readonly [formField: string]: string | boolean | null;
    };
  };
  readonly supportChatSessionNoteDrafts: {
    readonly [supportSessionId: number]: SupportChatSessionNoteDraft | null;
  };
  readonly deloitteDemo: boolean; // Used to show hidden Deloitte features for demonstration purposes.
  readonly postProjectObject: ProjectViewProject | null; // We can stop using this once login/signup is in webapp
  readonly webappChats: readonly SavedChatAttributes[];
  readonly webappChatDraftMessages: {
    readonly [threadIdentifier: string]: DraftMessage | null;
  };
  readonly webappThreadListMinimise: boolean;
  readonly taskList: TaskListState | null;
  readonly taskListCurrentTaskClean: boolean | null;

  readonly lastSeenPjpAssistant: LastSeenPjpAssistant;
  readonly hireMeDraft: HireMeDraft | null;

  readonly navSavedAlertsLastReadTime: number | null;
  readonly navUpdatesLastReadTime: number | null;
  readonly navNotificationUpdatesLastReadTime: number | null;

  readonly dashboardMyProjectsUserType: RoleApi | null;

  readonly enterpriseContactFormSubmitted: boolean; // Used on the enterprise landing page

  readonly postJobPageDraft: PostJobPageFormStates | null;

  readonly manageRecentTable: ManageRecentTable | null;

  readonly inviteToBidDiscardedFreelancers: {
    readonly [projectId: number]: readonly number[];
  };

  readonly flCardsExpandedStatus: {
    readonly [flCardId: string]: boolean;
  };

  readonly abTestVariations: ABTestVariationCache;

  readonly viewedItems: ViewedItemsMap;

  // Hide the mobile new message toast
  readonly hideMobileNewMessageToast: boolean;

  // Store user's messaging density preference
  readonly messagingDensityPreference: 'Default' | 'Comfortable';

  // Signed download URLs for messaging image tiles keyed by file UUID, bounded and TTL'd by MessagingAttachmentHelper.
  readonly messagingTileImageUrls: MessagingTileImageUrlCache;

  readonly searchServiceFilters: SearchFilters;

  // For saving the user's time tracking session across tabs
  readonly currentTimeTrackingSession: CurrentTimeTrackingSession | null;

  /**
   * Used on the CV upload step of the freelancer onboarding flow
   * TODO: T236205 Remove after endpoint allows fetching the uploaded file
   */
  readonly freelancerOnboardingResumeUploaded: boolean;

  // Parsed resume object from CV upload step of the freelancer onboarding flow
  readonly freelancerOnboardingParsedResume: UserParsedResume | null;

  // Used to check if CV is parsed successfully
  readonly freelancerOnboardingResumeParsingStatus: ResumeParsingStatus | null;

  // Use that whenever you need a unique ID for a particular device
  readonly deviceId: string | null;

  readonly dontAskAgainLastChecked: number;

  readonly nativeRatingPrompt: {
    // When was the last time the native rating prompt was shown?
    readonly lastPrompted: {
      readonly date: number;
      readonly leftFeedbackOrRating: boolean;
    };
    // If the user has been prompted to rate the app on project acceptance or
    // milestone release. This should only be asked for once.
    readonly oneOff: {
      readonly acceptProjectOrMilestoneRelease?: boolean;
    };
  };

  // For saving unfinished forms to be resumed later.
  readonly lastUsedForms: LastUsedForms;

  /* TYPES USED FOR TESTING ONLY */
  readonly testProperty: number;
  readonly testName: string | null;
  readonly testProfile: MockProfile;
  readonly testArray: readonly number[] | null;
  readonly externalQuotation: ExternalQuotation | null;

  // For saving cookie to be used after 3DS completed
  readonly threeDSChallengeCookie: string;

  // For saving whether notification has been granted in the internal request prompt
  readonly notificationsInternalGranted: boolean;

  // For saving whether notifications have been requested at login before.
  readonly notificationsRequested: boolean;

  // For saving whether notifications have been registered to the backend before.
  readonly notificationsRegistered: boolean;
  // For saving whether the iOS PushKit token (Used for calling in the iOS) has been registered to the backend before
  readonly voipNotificationsRegistered: boolean;

  // For saving loads which are pending actions which is used for blocking contact details when viewing subsequent loads
  readonly loadIdPendingAction: number | null;

  readonly groupsHomepageSelectedView: GroupsViewFilterSelection;

  // For indicating the waiting screen steps
  readonly waitingScreenSteps: WaitingScreenSteps;

  readonly giveGetReferrerUsername: string | null;
  readonly giveGetFooterClosed: boolean;

  readonly geolocationTrackingPrompted: boolean;

  // Store agent preferred ai prompt selection
  readonly aiPromptSelection: number | null;

  // For saving the chat history with AI Consultant
  readonly aiConsultantChatHistory: readonly AiConsultantMessage[] | null;
  // For saving the project data generated by AI Consultant
  readonly aiConsultantGeneratedProjectDraft: AiConsultantGeneratedProjectDraft | null;

  // For saving the conversation data in the AI Playground
  readonly aiPlaygroundConversationStorage:
    | readonly AiPlaygroundConversationSaveFile[]
    | null;

  // For saving currently saved tools in the AI playground
  readonly aiPlaygroundSavedTools: readonly AiPlaygroundSavedTool[] | null;

  // For saving the user's preferred theme
  readonly theme: ThemeSetting | null;

  // For saving the user's original theme (for use in pages with forced dark mode)
  readonly userOriginalTheme: Theme | null;

  // For recording the last time the user dismissed prompts to install the app that we want to remember between sessions
  readonly appInstallPromptDismissals: AppInstallPromptDismissals;
  // The number of days after which a user who dismissed an email install prompt should see it again
  readonly appInstallPromptEmailDismissalDays: number;

  // Used in contact syncing
  readonly doNotAskContactsSyncAgain: boolean;

  // Call state for incoming calls
  readonly videochatCalling: VideochatCalling | null;

  // Used in tracking consent banner
  readonly trackingConsent: TrackingConsent | null;

  readonly buildYourOwnAgentProjectDraft: BuildYourOwnAgentProjectDraft | null;

  readonly businessBuilderBlueprint: BusinessBuilderBlueprint | null;

  /**
   * Epoch ms until which the blueprint summary-report send button stays on
   * cooldown after a successful send.
   */
  readonly businessBuilderSummaryReportCooldownExpiry: number | null;

  readonly businessBuilderBlueprintMeta: BusinessBuilderBlueprintLocalMeta | null;

  readonly businessBuilderChatHistory:
    | readonly BusinessBuilderChatHistory[]
    | null;

  readonly doNotAskEnablePromptTwoFa: boolean;

  readonly loadshiftEmailBannerDismissed: boolean;

  readonly findTrucksAnnouncementLastSeen: number;

  readonly bitsComponentsViewMode: 'card' | 'table';

  readonly recentlyCreatedMilestoneTransactionIds: readonly number[] | null;

  readonly businessBuilderWorkspaceProjectTabVisitHistory:
    | readonly number[]
    | null;

  readonly businessBuilderWorkspaceReviewedAttemptIds: readonly string[] | null;

  readonly businessBuilderPjpContext: PostJobPageBlueprintContext | null;

  // Handed from the workspace to a fresh Post Job page: back-button target and
  // the attempt (and card, when posting from one) the post belongs to.
  readonly businessBuilderPjpReturn: {
    readonly attemptId: string;
    readonly workspaceUrl: string;
    readonly sourcePhaseId?: string;
    readonly sourceProjectId?: string;
  } | null;

  // Set after posting so the workspace scrolls that card into view next visit.
  readonly businessBuilderWorkspaceScrollProjectId: string | null;

  /* ESCROW LOCAL STORAGE TYPES */
  readonly escrowTransactionsPageFilters: {
    readonly formValues: TransactionFiltersRawFormValues;
    readonly searchQuery: string;
  } | null;
}

/*
 * The interfaces below are interfaces needed by `chat-box` and `helpers`
 */

export interface DraftMessage {
  readonly text: string;
  readonly lastUpdated: number;
}

/**
 * An in-progress support session note an agent has typed but not yet submitted.
 * `isEditing` distinguishes a draft typed into the "Add a note" form from one
 * typed into the edit-the-current-note form, so it is restored into the control
 * the agent was actually using.
 */
export interface SupportChatSessionNoteDraft {
  readonly note: string;
  readonly isEditing: boolean;
  readonly lastUpdated: number;
}

export enum FocusState {
  Header,
  HeaderAndTypingBox,
  Neither,
}

export interface SavedChatAttributes {
  readonly minimised: boolean;
  readonly focus: FocusState;
  readonly width: number;
  readonly height: number;
  readonly threadIdentifier: ThreadIdentifier;
  // This will be used in the new chatbox. `width` and `height`
  // will be deprecated once we complete the chatbox migration
  readonly dimensions?: ChatBoxDimensions;
  readonly origin?: string;
  readonly timeCreated?: number;
}

export interface WaitingScreenSteps {
  readonly projectId: number;
  readonly formStep: number;
  readonly vehicleStep: number;
}

export interface ChatBoxDimensions {
  readonly width: number;
  readonly height: number;
}

export interface ThreadIdentifier {
  readonly id: number; // TODO: T267853 - consider making id possibly undefined and getting rid of isFake
  readonly threadType: ThreadType;
  readonly context: ThreadContext;
  readonly members: readonly number[];
  readonly isFake: boolean; // TODO: T267853 - needed?
}

/* Interface used for TESTING ONLY */
interface MockProfile {
  readonly id: number;
  readonly name: string;
}

export interface HireMeDraft {
  readonly budget: number;
  readonly currencyId: number;
  readonly freelancerId: number;
  readonly hireFor: string;
  readonly hoursPerWeek: number;
  readonly message: string;

  // Optional field for backward compatibility.
  readonly confirmed?: boolean;
}

export interface LastSeenPjpAssistant {
  readonly time: number;
}

export interface TaskListState {
  readonly currentTaskClean?: boolean;
  readonly draftTask?: Partial<Task> | Task;
}

/**
 * Interface used for remembering recently viewed tables in /manage
 */
export interface ManageRecentTable {
  readonly employerTable: string;
  readonly collaboratorTable: string;
  readonly freelancerTable: string;
  readonly insourceTable: string;

  // This is the recently viewed table in general
  readonly manageTable?: string;
}

// To store the external sending data (i.e. email, phone, etc)
export interface ExternalQuotation {
  readonly email: string;
}

/**
 * Interfaces used for Generic Form Save functionality.
 */

export interface LastUsedForms {
  // The form section ID will be used as the key here (set by developer).
  // Inside the sectionId value it will contain the FormGroup object and its
  // whole contents.
  readonly [sectionId: string]: LastUsedFormsSection;
}

export interface LastUsedFormsSection {
  readonly [fieldId: string]: any;
}

/**
 * Interface used for AB Test caching
 */
export interface ABTestVariation<T extends keyof Experiments> {
  readonly variation: Experiments[T];
  readonly expiry: number;
}

export interface ABTestVariationCache {
  readonly [cacheKey: string]: ABTestVariation<keyof Experiments>;
}

export interface CachedTileImageUrl {
  readonly url: string;
  // ms-epoch expiry from the URL's `expires` param, or a fallback TTL when it carries none.
  readonly expires: number;
  // ms-epoch of the last read/write, used for LRU eviction.
  readonly lastAccessed: number;
}

export interface MessagingTileImageUrlCache {
  readonly [fileUuid: string]: CachedTileImageUrl;
}

// Sync metadata for the locally edited Business Builder draft. Kept here rather
// than in @freelancer/business-builder so local-storage does not depend on it.
export interface BusinessBuilderBlueprintLocalMeta {
  readonly blueprintUuid: string;
  // Last server version known for this uuid, 0 until the first sync.
  readonly version: number;
  readonly dirty: boolean;
  // UNIX seconds.
  readonly updatedAt: number;
}

/**
 * Interface for group homepage view state
 */
export type GroupsViewFilterSelection = 'grid' | 'list';

export type ArrayLocalStorageKeys = {
  [K in keyof LocalStorageTypes]: LocalStorageTypes[K] extends
    | readonly (infer _)[]
    | null
    ? K
    : LocalStorageTypes[K] extends readonly (infer _)[]
      ? K
      : never;
}[keyof LocalStorageTypes];

export interface BuildYourOwnAgentProjectDraft {
  readonly projectTitle: string;
  readonly projectDescription: string;
}
